/**
 * Per-character / per-dialogue voice playback for the Script Editor
 * (_GLOBAL.md G8 — the standalone Sound panel was folded in here).
 *
 * Each dialogue line's ▶ plays in the speaking character's voice: we use
 * the character's `VoiceProfile` (a cloned/uploaded `clonedVoiceId` when
 * present, otherwise a deterministic catalog voice keyed off the
 * character id so each character sounds consistent and distinct) and
 * layer the line's own SOUND edit (delivery / tone / speed) on top.
 *
 * The shared `TTSPlayer` only accepts catalog voice *keys*, not a raw
 * cloned voice id or a per-line playback rate, so this is a focused
 * sibling of its fetch+play path rather than a reuse — kept minimal and
 * routed through the same Worker `/tts` route.
 */

import {
  DEFAULT_VOICE_KEY,
  findVoiceCandidate,
  VOICE_CANDIDATES,
  type ElevenLabsVoiceSettings,
} from "../../output/voices";
import type { StudioCharacter } from "../StudioStore";
import type { LineSound } from "./scriptSidecars";

export interface ResolvedVoice {
  voiceId: string | undefined;
  voiceSettings: ElevenLabsVoiceSettings;
  playbackRate: number;
  /** Human label, for the tooltip / UI. */
  label: string;
}

/** Deterministically pick a catalog voice for a character id. */
function catalogVoiceForCharacter(characterId: string): {
  voiceId: string | undefined;
  voiceSettings: ElevenLabsVoiceSettings;
  playbackRate: number;
  label: string;
} {
  const candidates = VOICE_CANDIDATES.length > 0 ? VOICE_CANDIDATES : [];
  const fallback = findVoiceCandidate(DEFAULT_VOICE_KEY);
  if (candidates.length === 0 || !fallback) {
    return {
      voiceId: fallback?.voiceId,
      voiceSettings: fallback?.voiceSettings ?? {
        stability: 0.4,
        similarity_boost: 0.75,
        style: 0.5,
        use_speaker_boost: true,
      },
      playbackRate: fallback?.playbackRate ?? 1,
      label: fallback?.label ?? "default",
    };
  }
  let hash = 0;
  for (let i = 0; i < characterId.length; i += 1) {
    hash = (hash * 31 + characterId.charCodeAt(i)) | 0;
  }
  const pick = candidates[Math.abs(hash) % candidates.length];
  return {
    voiceId: pick.voiceId,
    voiceSettings: pick.voiceSettings,
    playbackRate: pick.playbackRate,
    label: pick.label,
  };
}

/** Resolve the voice for a character, honouring its VoiceProfile + a line sound. */
export function resolveVoice(
  character: StudioCharacter | null,
  sound: LineSound | null,
): ResolvedVoice {
  const base = character
    ? catalogVoiceForCharacter(character.id)
    : (() => {
        const f = findVoiceCandidate(DEFAULT_VOICE_KEY);
        return {
          voiceId: f?.voiceId,
          voiceSettings: f?.voiceSettings ?? {
            stability: 0.4,
            similarity_boost: 0.75,
            style: 0.5,
            use_speaker_boost: true,
          },
          playbackRate: f?.playbackRate ?? 1,
          label: f?.label ?? "default",
        };
      })();

  // A cloned/uploaded voice on the character's profile wins.
  const clonedVoiceId = character?.voiceProfile?.clonedVoiceId;
  const voiceId = clonedVoiceId || base.voiceId;

  // Layer the per-line sound edit: delivery nudges expressiveness; speed
  // maps to playback rate.
  const settings: ElevenLabsVoiceSettings = { ...base.voiceSettings };
  let rate = base.playbackRate;
  if (sound) {
    if (typeof sound.speed === "number" && Number.isFinite(sound.speed)) {
      rate = clamp(rate * sound.speed, 0.5, 2);
    }
    switch (sound.delivery) {
      case "whisper":
        settings.stability = clamp(settings.stability + 0.25, 0, 1);
        settings.style = clamp(settings.style - 0.2, 0, 1);
        break;
      case "shout":
        settings.stability = clamp(settings.stability - 0.2, 0, 1);
        settings.style = clamp(settings.style + 0.3, 0, 1);
        break;
      case "warm":
        settings.style = clamp(settings.style + 0.1, 0, 1);
        break;
      default:
        break;
    }
  }
  return { voiceId, voiceSettings: settings, playbackRate: rate, label: base.label };
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

/**
 * A minimal serial line player: one active audio element at a time,
 * fetches MP3 from `/tts` with the resolved voice, plays it, and reports
 * start/stop via callbacks so the button can pulse.
 */
export class LineVoicePlayer {
  private readonly workerBaseUrl: string;
  private activeAudio: HTMLAudioElement | null = null;
  private activeBlobUrl: string | null = null;

  constructor(workerBaseUrl: string) {
    this.workerBaseUrl = workerBaseUrl;
  }

  /** Stop any active playback immediately. */
  stop(): void {
    if (this.activeAudio) {
      this.activeAudio.pause();
      this.activeAudio.currentTime = 0;
      this.activeAudio = null;
    }
    if (this.activeBlobUrl) {
      URL.revokeObjectURL(this.activeBlobUrl);
      this.activeBlobUrl = null;
    }
  }

  /**
   * Speak `text` in `voice`. Resolves when playback ends (or fails). A
   * new call cancels any in-flight playback first.
   */
  async speak(
    text: string,
    voice: ResolvedVoice,
    hooks: { onStart?: () => void; onEnd?: () => void } = {},
  ): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return;
    this.stop();
    let blob: Blob | null = null;
    try {
      const response = await fetch(
        `${this.workerBaseUrl.replace(/\/$/, "")}/tts`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            text: trimmed,
            model_id: "eleven_flash_v2_5",
            voice_id: voice.voiceId,
            voice_settings: voice.voiceSettings,
          }),
        },
      );
      if (!response.ok) throw new Error(`/tts returned ${response.status}`);
      blob = await response.blob();
    } catch (error) {
      console.warn("[studio] line voice fetch failed:", error);
      hooks.onEnd?.();
      return;
    }

    const blobUrl = URL.createObjectURL(blob);
    this.activeBlobUrl = blobUrl;
    const audio = new Audio(blobUrl);
    audio.playbackRate = voice.playbackRate;
    this.activeAudio = audio;

    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        if (this.activeBlobUrl === blobUrl) {
          URL.revokeObjectURL(blobUrl);
          this.activeBlobUrl = null;
        } else {
          URL.revokeObjectURL(blobUrl);
        }
        if (this.activeAudio === audio) this.activeAudio = null;
        hooks.onEnd?.();
        resolve();
      };
      audio.addEventListener("play", () => hooks.onStart?.());
      audio.addEventListener("ended", finish);
      audio.addEventListener("error", finish);
      audio.play().catch(() => finish());
    });
  }
}
